import { AbstractControl, FormGroup } from '@angular/forms';

import {
  Dimension,
  Period,
  VisibilityCell,
  isCellVisible,
} from '../services/mbrs/validators/dimension-visibility.util';

/**
 * Options for {@link revealValidationErrors}.
 */
export interface RevealValidationErrorsOptions {
  /**
   * Answers "is this control populated by CalculationService rather than by the
   * preparer?" — normally `control.ui.calculated || control.ui.readonly` read
   * off the sheet's own schema. Computed controls are revealed only when they
   * carry a REAL error; see the `required`-only carve-out below.
   *
   * Omit it and every control is treated as preparer-entered.
   */
  isComputed?: (controlName: string) => boolean;
}

/**
 * Make a sheet's already-computed validation errors VISIBLE, deterministically,
 * at the moment the sheet's form is built and populated.
 *
 * ## Why this exists
 *
 * Every sheet's inline error markup is gated on `invalid && touched`, so an
 * error that exists on a freshly-hydrated form renders nothing until the user
 * interacts with the field. The only thing that used to mark a loaded sheet as
 * touched was the filing-form container's `triggerValidationForCurrentSheet()`,
 * fired from a wall-clock `setTimeout`. That call reads the sheet's FormGroup
 * off the child component and silently no-ops when the property is still
 * `undefined` — which is the normal state during a sheet's `async ngOnInit`,
 * because the form is only assigned after its schema fetches resolve. Lose that
 * race and NO field on the sheet ever shows an error. Scope of Filing lost it
 * most often: it awaits three fetches (including a ~640 KB MSIC options file)
 * before `buildForm()` runs.
 *
 * The fix is ownership, not a longer timeout: the sheet knows when its form is
 * ready, so the sheet calls this. Roles 0–4, 6 and 7 get it for free through
 * `finalizeSheetInit`, which already runs after hydration resolves.
 *
 * ## The invariant that makes this safe
 *
 * **Revealing must never move a sheet's published verdict.** Every
 * component-derived sheet publishes `hasErrors: !form.valid`, and Angular's
 * `markAsTouched()` does not affect validity — so a tick can never turn into a
 * cross, nor a warning into an error, merely because messages became visible.
 * This util therefore only ever marks controls touched: it does not add, arm or
 * clear validators, does not call `updateValueAndValidity` (nothing here can
 * change a validity result, so recomputing it would be pure risk), does not
 * mark anything dirty, and emits no events. `validation-reveal.util.spec.ts`
 * pins all five.
 *
 * The sheets whose status rollup DOES read `touched` (`invalid && (dirty ||
 * touched)` on roles 8, 9, 15-410000 and 23) are every one of them on
 * `PURE_VERDICT_SHEETS`, so that rollup is not the published verdict — the pure
 * validator's verdict against state is. Adding a sheet to that touched-reading
 * pattern while it is NOT allow-listed would break this guarantee.
 *
 * Cross-sheet validator ARMING is deliberately not part of this util. Role 20 —
 * the only sheet with `requiredIfCrossSheet` controls — arms its own in
 * `applyCrossSheetRequired()` before calling this, because arming genuinely does
 * change validity and so must stay a per-sheet decision.
 */
export function revealValidationErrors(
  form: FormGroup | undefined,
  options: RevealValidationErrorsOptions = {}
): void {
  // A real FormGroup always carries `controls`; the second half of this guard is
  // for the partial form doubles used by sheet specs (e.g. `{ valid: false }` in
  // sheet-init-finalize.util.spec.ts), which must not crash a sheet's init.
  if (!form?.controls) {
    return;
  }

  for (const controlName of Object.keys(form.controls)) {
    const control = form.get(controlName);
    if (!control || control.disabled) {
      // A disabled control reports `valid` whatever its validators say, so it
      // can never render an error — consistent with the "disabled ⇒ no
      // validations" rule the filing-info sheets follow.
      continue;
    }

    if (options.isComputed?.(controlName) && hasOnlyRequiredError(control)) {
      // PO ruling: a computed control is revealed only when it carries an error
      // the preparer can act on. An empty total means "nothing to sum yet", not
      // "a field was skipped" — the same distinction `isMandatoryForValidation`
      // draws in required-field.util.ts. A real error on a total (the SOFP
      // "Total assets" vs "Total equity and liabilities" equality mismatch being
      // the worked example) still reveals.
      continue;
    }

    control.markAsTouched({ onlySelf: true });
  }
}

/**
 * Minimal shape this util needs off a sheet schema — structural, so every
 * sheet's own local `SchemaControl` interface satisfies it without importing a
 * shared model (the sheets deliberately declare their own).
 */
interface ComputedFlagCarrier {
  qname: string;
  // `ui` is deliberately `unknown` rather than a flag shape. The 24 sheets each
  // declare their OWN closed `SchemaControl`/`FormControlSchema` interface, and
  // they disagree: some name `calculated`/`readonly`, most name neither. A
  // structural flag type is rejected by weak-type detection for the schemas that
  // omit both, and an index signature is rejected by the ones that declare a
  // closed `FormControlUI`. Narrowing happens at the single read site below,
  // which is safe because a missing flag reads as `undefined` either way.
  ui?: unknown;
}

/**
 * Builds the `isComputed` predicate for {@link revealValidationErrors} from a
 * sheet's schema controls, so each sheet passes one expression rather than
 * hand-rolling the same `ui.calculated || ui.readonly` read.
 *
 * A control absent from the schema (a hand-written input, or one of role 2's
 * hard-coded MSIC/CLBG fields) is treated as preparer-entered — those are typed
 * by the user, so their errors must always reveal.
 */
export function computedControlPredicate(
  controls: readonly ComputedFlagCarrier[] | undefined
): (controlName: string) => boolean {
  const computed = new Set<string>();
  for (const control of controls ?? []) {
    const ui = control.ui as { calculated?: boolean; readonly?: boolean } | undefined;
    if (ui?.calculated || ui?.readonly) {
      computed.add(control.qname);
    }
  }
  return (controlName: string) => computed.has(controlName);
}

/**
 * True when the control's only complaint is that it is empty. Used to suppress
 * the mandatory-field message on computed controls without suppressing their
 * real errors.
 */
function hasOnlyRequiredError(control: AbstractControl): boolean {
  const errors = control.errors;
  if (!errors) {
    // No error at all: nothing to reveal, and a `required` error is the only
    // thing a computed control could later acquire on its own. Treat it as the
    // required-only case so a computed control stays quiet until a validator
    // gives it something real.
    return true;
  }
  const keys = Object.keys(errors);
  return keys.length === 1 && keys[0] === 'required';
}

/**
 * Keep a control's PRESENTATION state honest about its validity, after
 * something other than the preparer emptied it.
 *
 * The mount-time {@link revealValidationErrors} pass makes a loaded sheet's
 * errors visible, but a later cascade can undo it: a driver dropdown cleared
 * via its `[showClear]` "X" blanks the field it drives, and the paired-field
 * watchers on roles 4 and 5 used to follow that by marking the emptied control
 * untouched AND pristine. Both inline-message gates read `dirty || touched`, so
 * a field that was still `required` went silent while continuing to block
 * submission — a sheet painted green-ish with a red sheet-selector cross, and
 * no field to point at. Standardised by P.O. ruling 2026-08-24: a mandatory
 * field shows its error however it came to be empty.
 *
 * So: still invalid => reveal it (the preparer has something to act on);
 * valid => reset touched/pristine, which is the original 2026-07-29 intent
 * (a field whose selection no longer exists should not look like a failure).
 *
 * Like {@link revealValidationErrors} this NEVER moves validity — it neither
 * seats nor clears validators and does not call `updateValueAndValidity`, so a
 * sheet's published `hasErrors` cannot change because a message became
 * visible. Call it AFTER the validators for the new state have been seated.
 *
 * A disabled control is skipped: it reports `valid` whatever its validators
 * say and can never render an error, per the "disabled ⇒ no validations" rule.
 */
export function syncRevealToValidity(
  control: AbstractControl | null | undefined
): void {
  if (!control || control.disabled) {
    return;
  }

  if (revealIfInvalid(control)) {
    return;
  }

  control.markAsUntouched({ onlySelf: true });
  control.markAsPristine({ onlySelf: true });
}

/**
 * Reveal a control's error if it has one, and otherwise leave its presentation
 * state completely alone. Returns whether it revealed.
 *
 * This is the half of {@link syncRevealToValidity} that is safe to call from a
 * validator RE-SEAT — role 5's `updateConditionalFieldsVisibility`, which runs
 * on every keystroke in the "other person" trio. The reset-when-valid branch
 * must NOT run there: the control that triggered the re-seat is the one the
 * preparer is typing into, and marking it pristine mid-edit would throw away
 * state the sheet's own cancel/revert path relies on.
 *
 * Like everything else in this file it cannot move validity.
 */
export function revealIfInvalid(
  control: AbstractControl | null | undefined
): boolean {
  if (!control || control.disabled || control.valid) {
    return false;
  }
  control.markAsTouched({ onlySelf: true });
  return true;
}

/**
 * Enable a control for inline editing and immediately reveal any error it turns
 * out to be carrying.
 *
 * ## Why enabling alone is not enough
 *
 * The ten inline-edit sheets build every amount control `disabled: true` and
 * enable them only when the preparer clicks the edit pencil. Angular gives a
 * DISABLED control `status: 'DISABLED'`, `invalid: false` and `errors: null`
 * whatever its validators say — so while the row is closed there is genuinely
 * nothing to show, and `revealValidationErrors` is right to skip it.
 *
 * `enable()` re-runs the validators, and THAT is the moment an error appears:
 * a hydrated value below its schema `min`, or an empty `required` cell, flips
 * the control to `invalid` — while `dirty` and `touched` are both still
 * `false`, because the preparer has not typed anything yet. Every one of those
 * sheets gates its inline message and row-error rollup on
 * `invalid && (dirty || touched)`, so the error stayed hidden until the user
 * happened to touch that exact cell. Measured:
 *
 * ```
 * new FormControl({ value: -5, disabled: true }, Validators.min(0))
 *   disabled -> invalid=false errors=null
 *   enable() -> invalid=true  errors={min:{min:0,actual:-5}} dirty=false touched=false
 * ```
 *
 * P.O. ruling 2026-08-26: a mandatory-or-invalid field shows its error however
 * it came to be in that state — so opening a row must not withhold it.
 *
 * ## Contract
 *
 * Enables with `{ emitEvent: false }` by default so the sheet's own
 * `valueChanges` edit pipeline is not re-entered by the act of opening a row;
 * pass `emitEvent: true` for a sheet that deliberately relies on that emission.
 * Like the rest of this file it can only mark a control TOUCHED — it never
 * seats or clears a validator, so a sheet's published verdict cannot move
 * because a row was opened.
 *
 * ## `reveal: false` — the column is not on screen
 *
 * The dual-column sheets build all FOUR dimension controls
 * (Consolidated/Separate × Current/Previous) whatever the permutation renders,
 * and `enterEditMode` enables all four so the save and cancel paths round-trip
 * a whole row. A hidden column of a required row is permanently null, so
 * `enable()` makes it `invalid` — and revealing THAT paints the row red with
 * nothing to explain it, because every message surface (Error Description, the
 * inline text) is already filtered to visible columns. Reported from UAT on
 * 2026-08-26 against the two "Cash and cash equivalents at beginning/end of
 * period" rows on a filing with no previous year.
 *
 * So the caller passes `reveal: isCellVisible(...)`: enable regardless, reveal
 * only what the preparer can act on. The border and the text then read
 * different sources but the same SCOPE, which is the only way they can agree.
 */
export function enableAndRevealErrors(
  control: AbstractControl | null | undefined,
  options: { emitEvent?: boolean; reveal?: boolean } = {}
): void {
  if (!control) {
    return;
  }
  control.enable({ emitEvent: options.emitEvent ?? false });
  if (options.reveal ?? true) {
    revealIfInvalid(control);
  }
}

/**
 * The four dual-column cells every dual-column sheet builds, in the order those
 * sheets already enumerate them.
 */
export const DUAL_COLUMN_CELLS: ReadonlyArray<{
  readonly dimension: Dimension;
  readonly period: Period;
}> = [
  { dimension: 'Consolidated', period: 'CurrentYear' },
  { dimension: 'Consolidated', period: 'PreviousYear' },
  { dimension: 'Separate', period: 'CurrentYear' },
  { dimension: 'Separate', period: 'PreviousYear' },
];

/**
 * Open one row of a dual-column sheet: enable all four of its dimension
 * controls, and reveal the errors of the ones this permutation actually SHOWS.
 *
 * ## Why all four are enabled but only some revealed
 *
 * These sheets build the full 2×2 grid of controls whatever the filing renders,
 * and the save / cancel paths round-trip all four — so enabling is unconditional.
 * Revealing is not: a hidden column of a `required` row is permanently null, so
 * `enable()` flips it to `invalid`, and marking it touched satisfies the
 * `invalid && (dirty || touched)` gate that the row-error rollups read. The
 * result is a row painted red with an EMPTY Error Description, because every
 * message surface on these sheets filters hidden columns out. That is exactly
 * what UAT reported on 2026-08-26 against role 18's "Cash and cash equivalents at
 * beginning / end of period" rows on a filing with no previous year — the only
 * two required, non-calculated (hence pencil-editable) rows on that sheet.
 *
 * `visibility` is the sheet's own `dimensionVisibility` cells, so this stays in
 * step with the single resolver in `dimension-visibility.util.ts` rather than
 * re-deriving "is there a previous year" per sheet.
 *
 * Returns the four control names in `DUAL_COLUMN_CELLS` order, which is the same
 * order these sheets build their `originalValues` snapshot in.
 */
export function enableDualColumnRowAndRevealVisible(
  form: FormGroup | undefined,
  baseName: string,
  visibility: VisibilityCell[],
  options: { emitEvent?: boolean } = {}
): string[] {
  const names: string[] = [];
  for (const { dimension, period } of DUAL_COLUMN_CELLS) {
    const name = `${baseName}[${dimension}][${period}]`;
    names.push(name);
    enableAndRevealErrors(form?.get(name), {
      emitEvent: options.emitEvent,
      reveal: isCellVisible(visibility, dimension, period),
    });
  }
  return names;
}
